import { Location, NgClass, NgStyle } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, HostListener, inject, OnInit, viewChild, viewChildren } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { BREAKPOINT_MD, DashboardMenuService } from '../../../services';
import { filter } from 'rxjs/operators';
import { DashboardMenuItem } from '../../dashboard-frame-config';

@Component({
	selector: 'frame-menu',
	standalone: true,
	imports: [
		RouterModule,
		NgStyle,
		NgClass,
		RouterLink,
	],
	templateUrl: './menu.part.html',
	styleUrls: ['./menu.part.scss']
})

export class MenuPart implements OnInit, AfterViewInit {

	private location = inject(Location);
	public menu = inject(DashboardMenuService);
	public router = inject(Router);

	public menuItems = computed(() => this.menu.menuItems());
	public menuHeight: number = 0; // Initialize
	public isMenuShouldCollapsed = false;
	public showHoverElem: boolean = false;
	public hoverElemHeight: number = 0;
	public hoverElemTop: number = 0;
	private readonly menuContainer = viewChild<ElementRef>('menuContainer');
	private readonly expandableMenuItems = viewChildren<ElementRef>('.sidebar-item-expandable');


	constructor() {
		this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe(() => this.syncMenu());
	}

	ngOnInit(): void {
		if (this.shouldMenuCollapse()) this.menu.setMenuCollapsed(true);
	}

	ngAfterViewInit(): void {
		this.updateSidebarHeight();
		this.syncMenu();
	}


	syncMenu() {
		this.openMenuToLocation();
		const width = (window.innerWidth > 0) ? window.innerWidth : screen.width;
		if (width <= 768) this.menu.setMenuCollapsed(true);
		window.scrollTo(0, 0);
	}

	@HostListener('window:resize')
	public onWindowResize(): void {
		const isMenuShouldCollapsed = this.shouldMenuCollapse();

		if (this.isMenuShouldCollapsed !== isMenuShouldCollapsed) {
			this.menu.setMenuCollapsed(isMenuShouldCollapsed);
		}
		this.isMenuShouldCollapsed = isMenuShouldCollapsed;
		this.updateSidebarHeight();
	}

	private shouldMenuCollapse(): boolean {
		return window.innerWidth <= BREAKPOINT_MD;
	}

	public updateSidebarHeight(): void {
		if (this.menuContainer()) { // Check if menuContainer is available
			this.menuHeight = this.menuContainer()!.nativeElement.clientHeight - 84;
		}
	}

	public hoverItem($event: MouseEvent): void {
		this.showHoverElem = true;
		this.hoverElemHeight = ($event.target as HTMLElement).clientHeight;
		this.hoverElemTop = ($event.target as HTMLElement).getBoundingClientRect().top - 50;
	}

	public unHoverItem(): void {
		this.showHoverElem = false;
	}

	public collapseMenu(event: Event): boolean {
		const link = event.currentTarget as HTMLElement;
		const parent = link.parentElement;

		if (this.menu.menuCollapsed()) {
			this.menu.setMenuCollapsed(false);

			if (parent?.classList.contains('sidebar-item-expanded')) {
				return false;
			} else {
				parent?.parentElement?.querySelectorAll('li').forEach(li => li.classList.remove('sidebar-item-expanded'));
				parent?.parentElement?.querySelectorAll('li .sidebar-sublist').forEach(submenu => (submenu as HTMLElement).style.display = 'none'); // Hide submenus
				parent?.classList.add('sidebar-item-expanded');
				setTimeout(() => {
					const nextSibling = link.nextElementSibling as HTMLElement;
					if (nextSibling) {
						nextSibling.style.display = 'block';
					}
				}, 250);
				const nextSibling = link.nextElementSibling as HTMLElement;
				if (nextSibling) {
					nextSibling.style.display = 'none'; // Ensure it's hidden before sliding down
					(nextSibling as HTMLElement).style.display = 'block'; // Ensure it's hidden before sliding down
				}
				if (link.nextElementSibling as HTMLElement) (link.nextElementSibling as HTMLElement).style.display = 'block';
			}
		} else {
			if (parent?.classList.contains('sidebar-item-expanded')) {
				parent?.classList.remove('sidebar-item-expanded');
				if (link.nextElementSibling as HTMLElement) (link.nextElementSibling as HTMLElement).style.display = 'none';
			} else {
				parent?.parentElement?.querySelectorAll('li').forEach(li => li.classList.remove('sidebar-item-expanded'));
				parent?.parentElement?.querySelectorAll('li .sidebar-sublist').forEach(submenu => (submenu as HTMLElement).style.display = 'none');
				parent?.classList.add('sidebar-item-expanded');
				if (link.nextElementSibling as HTMLElement) (link.nextElementSibling as HTMLElement).style.display = 'block';
			}
		}
		return false;
	}



	public resetMenus(browserUrl: string) {

		const menuItems = this.expandableMenuItems();

		menuItems.forEach(menuItem => {
			const dataUrl = menuItem.nativeElement.dataset.url;
			if (dataUrl) {

				const isMatching = browserUrl === dataUrl || browserUrl.startsWith(dataUrl + '/');
				menuItem.nativeElement.classList.toggle('sidebar-item-expanded', isMatching); // Use toggle

				const subMenu = menuItem.nativeElement.querySelector('ul');
				if (subMenu) {
					(subMenu as HTMLElement).style.display = isMatching ? 'block' : 'none'; // Conditional display
				}
			}
		});

	}

	public openMenuToLocation(): void {
		const url = this.location.path();

		setTimeout(() => {
			this.resetMenus(url);
			window.scrollTo(0, 0);

			const activeLink = document.querySelector(`a[href="${url}"]`);

			if (activeLink) {
				let currentElement: HTMLElement | null = activeLink as HTMLElement;
				while (currentElement) {
					if (currentElement.tagName === 'LI') {
						currentElement.classList.add('sidebar-item-expanded');
						const subMenu = currentElement.querySelector('ul') as HTMLElement;
						if (subMenu) {
							subMenu.style.display = 'block';
						}
					}
					currentElement = currentElement.parentElement;
				}
			}
		});
	}


	public isActive(url: string): boolean {
		return this.router.url === url;
	}

	public isActiveMenu(item: DashboardMenuItem) {
		if (item.subMenu && item.subMenu.length > 0) {
			return this.router.url === item.routerLink;
		}
		else {
			return this.router.url.startsWith(item.routerLink ?? '');
		}
	}

	public isExpanded(item: DashboardMenuItem) {

		const browserUrl = this.router.url;
		const itemUrl = item.routerLink;
		const hasSubMenu = !!(item.subMenu && item.subMenu.length);

		if (!itemUrl) return false;
		if (!hasSubMenu) return false;

		return browserUrl.startsWith(itemUrl);
	}
}